/**
 * Bucket-scoped exclusion-reason filter encoding (PREC-3536 Phase 5).
 *
 * The lead-list "Show" dropdown filters by exclusion reason *within* an inclusion-status
 * bucket. A bare `filterParam` is ambiguous — e.g. "company" backs "Doesn't work there
 * anymore" (UNSURE) *and* "Never worked there" (NOT_FOUND) — so the wire format scopes every
 * reason to its bucket as a single string: `"NOT_FOUND:jobTitle"`. Web encodes, API decodes;
 * sharing the codec keeps the two sides from drifting.
 */

/** One decoded bucket-scoped reason filter. */
export interface ExclusionReasonFilter {
	/** The `list_person.inclusionStatus` bucket the reason belongs to (e.g. "NOT_FOUND"). */
	inclusionStatus: string;
	/** The `exclusion_reason.filterParam` within that bucket (e.g. "jobTitle"). */
	filterParam: string;
}

/** One per-(bucket, reason) count row returned on the lean list info. */
export interface ExclusionReasonCount extends ExclusionReasonFilter {
	/** SQL aggregate — arrives as a string at runtime; coerce before arithmetic. */
	count: number;
}

/**
 * Synthetic `filterParam` for a lead carrying no exclusion reason at all (PREC-3871).
 *
 * The Show dropdown's reason children have to partition their bucket — they are rendered as a
 * breakdown of it — so a lead with no recorded reason still needs a child to belong to. The API
 * COALESCEs to this value when counting and when filtering rows; the web renders it as "Other".
 * Shared here because both sides must agree on the exact token, and it travels the wire inside
 * `exclusionReasons` like any real reason (it is a safe SQL identifier for that reason).
 */
export const NO_REASON_FILTER_PARAM = "__none__";

/**
 * The `exclusion_reason.filterParam` vocabulary (PREC-3871).
 *
 * `filterParam` is a free-form column, so for a long time each writer coined its own value and
 * the web's label map was updated by hand — until it wasn't: `contactInfo` had no entry, so the
 * dropdown rendered the raw param as its label ("ContactInfo") next to properly translated
 * siblings. Naming them once, here, lets the web assert it can label every one of them instead of
 * silently falling back.
 *
 * Values NOT listed here still work — the dynamic ICP filter params (`prospect.seniority`,
 * `organization.industry`, …) are generated from a list's config and cannot be enumerated. This
 * covers the fixed vocabulary the pipeline writes deliberately.
 */
export const EXCLUSION_REASON_PARAMS = {
	/** Title screened out before enrichment; recoverable via "Enrich anyway". */
	jobTitle: "jobTitle",
	/** A named person we could not find on LinkedIn or reach by email. */
	person: "person",
	/** The lead has neither a LinkedIn URL nor a usable email. */
	contactInfo: "contactInfo",
	/** No longer works at the company (Unsure). Historically also carried "never worked there". */
	company: "company",
	/**
	 * The found person never worked at the uploaded company. Its own token because it now lands
	 * in Unsure (when the person has a LinkedIn URL or email) beside `company`, and the Show
	 * dropdown labels a bucket's reasons by param — sharing `company` would file these under
	 * "Doesn't work there anymore".
	 */
	wrongCompany: "wrongCompany",
	/** Did not match the title search criteria. */
	title: "title",
	/** Did not match the country search criteria. */
	country: "country",
	/** An uploaded company row that never resolved to an organization. */
	companyNotFound: "companyNotFound",
	/** The organization resolved, but no person matched the search. */
	personNotIdentified: "personNotIdentified",
	/**
	 * "Find new people only" (PREC-3899): every person Company Enrich offered for the company is
	 * already in the workspace's Contacts, so nobody new was found. Its own token because it is
	 * not "nobody there": the people exist, and the customer holds them already.
	 */
	noNewPerson: "noNewPerson",
	/** ICP-filtered out, so no person was ever searched for. */
	icpCompanyMismatch: "icpCompanyMismatch",
	/** Enrichment threw before the row could resolve — retryable. */
	processingError: "processingError",
	/**
	 * A filter of the list could not be read, so no lead passed it (PREC-4622). A fixed token: the
	 * filter's key is free text a list owner chose, and only the reason text names it, since every
	 * param here is interpolated into SQL (see `inOtherList` below, PREC-4094).
	 */
	unreadableFilter: "unreadableFilter",
	/**
	 * Enrichment failed on our side because the row's own data cannot be stored (a value too long
	 * for its column, an invalid character, a required value missing). Ours, never charged — but a
	 * Retry would fail the same way, so none is offered (PREC-4520).
	 */
	processingErrorPermanent: "processingErrorPermanent",
	/**
	 * The person's LinkedIn profile could not be read and no position of theirs is stored, so whether
	 * they work at the uploaded company was never checked (PREC-4522). Ours, not a verdict about the
	 * person: the lead is Unsure ("Employer could not be verified"), never "Never worked at the
	 * company" (`wrongCompany`), which is what such a lead was filed as before.
	 */
	employerUnverified: "employerUnverified",
	/**
	 * The AI company-match comparison returned an affirmative verdict with an uncertain confidence
	 * score (50..74), so the proposed company match was left unresolved in Unsure (PREC-4283).
	 */
	companyMatchUncertain: "companyMatchUncertain",
	/**
	 * The person's employer matched the list's company by name only, and the name is not unique in
	 * the customer's own lists or contradicts the company page the role links, so the match was
	 * left unresolved in Unsure (PREC-4283).
	 */
	companyNameAmbiguous: "companyNameAmbiguous",
	/**
	 * The person's current employer is known by name only, and no company of the customer's own
	 * lists carries that name, so the company was left unresolved in Unsure (PREC-4283).
	 */
	companyNotIdentified: "companyNotIdentified",
	/**
	 * The lead is already on another list of the company, so `list.onlyNewLeads` kept it off this
	 * one (PREC-4094). It is the only reason attached to a `SKIPPED` lead, and the reason text names
	 * the lists it was found on.
	 *
	 * Note what this replaced: the writer coined the free-text `filterParam` `"In another list"`.
	 * Every value here is interpolated into SQL and validated with `isSafeSqlIdentifier`, so a
	 * param with spaces is *counted* by the Show dropdown and then silently **dropped** by the row
	 * filter — which falls back to matching the whole bucket. 15,078 production rows carried it, and
	 * clicking the child counted at 882 returned all 922.
	 */
	inOtherList: "inOtherList",
	/**
	 * The three blacklist verdicts (PREC-4083). Unlike every param above they describe why a lead
	 * is on the **Blacklist** bucket, not why it failed to qualify — the API only ever counts them
	 * there, and never counts the others there (`buildReasonBucketScopeSql`). Before them a
	 * blacklisted lead carried no reason of its own, so 91% of production's blacklisted leads fell
	 * into the "Other" child while the reasons from their previous bucket were shown as the
	 * breakdown of Blacklist.
	 */
	competitor: "competitor",
	doNotContact: "doNotContact",
	unsubscribed: "unsubscribed",
	/**
	 * A user moved the lead to Unsure by hand — the bulk bar's "Exclude from list" (PREC-4172).
	 * Until then a manual exclusion left no trace the product shows: the lead landed under
	 * Unsure → "Other" with no author and no time, and the only record was `audit_log`, which is
	 * pruned at 30 days and surfaced nowhere. A customer whose colleague had excluded 17 leads
	 * reported that the AI sequence generation had moved them. The reason text names the user.
	 */
	manuallyExcluded: "manuallyExcluded",
	/**
	 * A user took the lead off the blacklist to contact it (PREC-2548): the override modal on
	 * assign, compose, reply and the bulk status moves. The reason text names the user. It explains
	 * a lead in Found, so it is counted there and nowhere else ({@link ADDED_REASON_PARAMS}).
	 */
	includedByUser: "includedByUser",
} as const;

export type ExclusionReasonParam =
	(typeof EXCLUSION_REASON_PARAMS)[keyof typeof EXCLUSION_REASON_PARAMS];

/**
 * The params that explain a **blacklist verdict** rather than a failed qualification (PREC-4083).
 *
 * They are countable only inside the `BLACKLIST` bucket, and every other param is countable only
 * outside it — the API enforces both halves in the one predicate the counts and the row filter
 * share, and the web uses this list to render the same reason in the Reason column. Shared here
 * because that agreement spans both apps.
 */
export const BLACKLIST_REASON_PARAMS: readonly string[] = [
	EXCLUSION_REASON_PARAMS.competitor,
	EXCLUSION_REASON_PARAMS.doNotContact,
	EXCLUSION_REASON_PARAMS.unsubscribed,
];

/** Whether a `filterParam` is one of the blacklist verdicts. */
export const isBlacklistReasonParam = (filterParam?: string | null): boolean =>
	!!filterParam && BLACKLIST_REASON_PARAMS.includes(filterParam);

/**
 * The params that explain a **SKIPPED** lead (PREC-4094) — the same shape as
 * {@link BLACKLIST_REASON_PARAMS}, for the same reason.
 *
 * `inclusionStatus` is a single column and several paths assign over it, so a reason outlives the
 * bucket it was written for. A lead added by hand from Compose is the clearest case: the guard
 * refuses it, records `inOtherList`, and `createNewListPersonFromProspect` then force-sets the row
 * back to `ADDED` — leaving a lead in **Found** explained by "Already on another list". Nothing has
 * to be deleted; the reason stays as the record of the earlier decision and simply stops being
 * counted where it does not apply.
 */
export const SKIPPED_REASON_PARAMS: readonly string[] = [EXCLUSION_REASON_PARAMS.inOtherList];

/** Whether a `filterParam` only ever explains a skipped lead. */
export const isSkippedReasonParam = (filterParam?: string | null): boolean =>
	!!filterParam && SKIPPED_REASON_PARAMS.includes(filterParam);

/**
 * The params that only ever explain a lead in **Found** (PREC-2548). Found takes every param but
 * the blacklist and skipped ones; these are the reverse case, kept out of every other bucket so a
 * lead moved on from Found is not explained by the decision that put it there.
 */
export const ADDED_REASON_PARAMS: readonly string[] = [EXCLUSION_REASON_PARAMS.includedByUser];

/** Whether a `filterParam` only ever explains a lead in Found. */
export const isAddedReasonParam = (filterParam?: string | null): boolean =>
	!!filterParam && ADDED_REASON_PARAMS.includes(filterParam);

/**
 * The params countable in a given bucket, or `null` for the buckets that take "everything else".
 * The single source both the SQL (`buildReasonBucketScopeSql`) and the web (`visibleExclusionReasons`)
 * resolve through, so the table and the Show dropdown cannot disagree.
 */
export const bucketExclusiveReasonParams = (
	inclusionStatus?: string | null
): readonly string[] | null => {
	if (inclusionStatus === "BLACKLIST") {
		return BLACKLIST_REASON_PARAMS;
	}

	if (inclusionStatus === "SKIPPED") {
		return SKIPPED_REASON_PARAMS;
	}

	return null;
};

/**
 * Whether a reason may explain a lead in the given bucket. The API's SQL predicate
 * (`buildReasonBucketScopeSql`) states the same rule; the in-memory resolvers on both sides call
 * this, so the table, the counts and the Show dropdown agree on which reason a lead shows.
 */
export const isReasonCountableInBucket = (
	filterParam?: string | null,
	inclusionStatus?: string | null
): boolean => {
	if (!filterParam) {
		return false;
	}

	const exclusive = bucketExclusiveReasonParams(inclusionStatus);

	if (exclusive) {
		return exclusive.includes(filterParam);
	}

	if (isBlacklistReasonParam(filterParam) || isSkippedReasonParam(filterParam)) {
		return false;
	}

	return inclusionStatus === "ADDED" || !isAddedReasonParam(filterParam);
};

const SEPARATOR = ":";

/** Encode a bucket-scoped reason filter for the `exclusionReasons` query param. */
export function encodeExclusionReasonFilter(inclusionStatus: string, filterParam: string): string {
	return `${inclusionStatus}${SEPARATOR}${filterParam}`;
}

/** Decode one `exclusionReasons` entry; null when the entry is not `STATUS:filterParam`. */
export function decodeExclusionReasonFilter(value: string): ExclusionReasonFilter | null {
	const separatorIndex = value.indexOf(SEPARATOR);

	if (separatorIndex <= 0 || separatorIndex === value.length - 1) {
		return null;
	}

	return {
		inclusionStatus: value.slice(0, separatorIndex),
		filterParam: value.slice(separatorIndex + 1),
	};
}
